/**
 * session.tsx — the adaptive, AI-led elicitation session. The AI reasons out each
 * question from the goal (scope) and the evidence gathered so far: it picks the next
 * instrument and writes the question. Answering commits an EvidenceItem and triggers
 * the next question. When the interviewer judges it has enough, you synthesize.
 */
import { useCallback, useEffect, useState } from "react";
import { useRegistry, useWorkspace } from "../lib/hooks.ts";
import { useAction } from "../lib/actions.ts";
import { runCommand } from "../lib/api.ts";
import { useI18n } from "../lib/i18n.tsx";
import { navigate } from "../lib/router.tsx";
import { Primitive, type EmitPayload } from "../primitives/Primitive.tsx";
import type { AiCallTrace, QuestionStimulus } from "../../ai/provider.ts";

interface NextQuestion {
  primitive: string;
  prompt: string;
  rationale: string;
  done: boolean;
  seed?: string;
  stimulus?: QuestionStimulus;
  subgoalId?: string;
  promptTrace?: AiCallTrace;
}

export function SessionRoute({ sessionId }: { sessionId: string }): JSX.Element {
  const reg = useRegistry();
  const { ws, reload } = useWorkspace();
  const { run, status, busy } = useAction();
  const { t, locale } = useI18n();
  const [question, setQuestion] = useState<NextQuestion | null>(null);
  const [thinking, setThinking] = useState(false);

  const session = ws?.session[sessionId];
  const asked = session?.turns.length ?? 0;

  const loadNext = useCallback(async (force = false) => {
    setQuestion(null);
    setThinking(true);
    const r = await runCommand("session.next", { sessionId, locale, ...(force ? { force: true } : {}) });
    setThinking(false);
    if (r.kind === "ok" && r.value.value) setQuestion(r.value.value as NextQuestion);
  }, [sessionId, locale]);

  // One deterministic loader: on session/locale change, refetch the workspace
  // (deep-link / out-of-band session) and load the first AI question.
  useEffect(() => {
    reload();
    void loadNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, locale]);

  if (!reg || !ws || !session) return <div className="wrap"><p className="mini">{t("common.loading")}</p></div>;

  const pack = reg.packs.find((p) => p.methodPackId === session.methodPackId);
  const primitive = question ? reg.primitives.find((p) => p.slug === question.primitive) : undefined;
  const plan = ws.plan?.[sessionId];
  const coveredCount = plan ? plan.subgoals.filter((s) => s.status === "covered").length : 0;

  const emit = async (payload: EmitPayload): Promise<void> => {
    if (!primitive) return;
    await run("evidence.capture", {
      sessionId,
      primitive: primitive.slug,
      summary: payload.summary,
      boundary: payload.boundary,
      rawContent: payload.rawContent,
    });
    setQuestion(null);
    await loadNext(); // adaptive: the next question is reasoned from updated evidence
  };

  return (
    <div className="wrap">
      <div className="page-head">
        <div>
          <div className="sec-no">{t("session.pack", { pack: pack?.name ?? "" })}</div>
          <h1 className="page-title">{session.title}</h1>
          <p className="page-desc">
            {t("session.scope", { scope: session.scope })} · {t("session.captured", { n: asked })}
          </p>
        </div>
        <div className="row">
          <span className={`pill ${session.status === "open" ? "ok" : ""}`}>{session.status}</span>
          <button className="btn ghost" onClick={() => navigate("/evidence")}>{t("session.viewEvidence")}</button>
        </div>
      </div>

      {plan && plan.subgoals.length > 0 ? (
        <div className="plan-strip" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "0 0 18px" }}>
          <span className="mini" style={{ opacity: 0.85 }}>
            {t("session.plan")}: {t("session.plan.covered", { n: coveredCount, m: plan.subgoals.length })}
          </span>
          {plan.subgoals.map((s) => {
            const active = question?.subgoalId === s.id;
            const dot = s.status === "covered" ? "var(--phosphor)" : s.status === "conflicted" ? "var(--amber)" : "var(--line)";
            return (
              <span key={s.id} className="pill" title={`${s.facet} · ${s.targetConstruct} · ${s.status}`} style={{ borderColor: active ? "var(--trace)" : undefined, opacity: s.status === "covered" ? 1 : 0.72 }}>
                <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 99, background: dot, marginRight: 6, verticalAlign: "middle" }} />
                {s.facet}
              </span>
            );
          })}
        </div>
      ) : null}

      {thinking ? (
        <div className="empty-note">✦ {t("session.thinking")}</div>
      ) : question && !question.done && primitive ? (
        <>
          <div className="ai-question-head">
            <span className="pill trace">{t("session.q", { n: asked + 1 })}</span>
            {question.rationale ? (
              <span className="ai-rationale">
                <b>{t("session.why")}:</b> {question.rationale}
              </span>
            ) : null}
          </div>
          <Primitive key={`${primitive.slug}-${asked}`} primitive={primitive} emit={emit} promptOverride={question.prompt} seed={question.seed} stimulus={question.stimulus} ws={ws} packSlug={pack?.slug} />
          {question.promptTrace ? (
            <details className="prompt-trace" style={{ marginTop: 12 }}>
              <summary className="mini" style={{ cursor: "pointer" }}>✦ {t("session.showPrompt")}</summary>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8, maxHeight: 340, overflow: "auto", border: "1px solid var(--line)", borderRadius: 8, padding: 12 }}>
{`${question.promptTrace.system}\n\n${question.promptTrace.instructions}\n\n— input —\n${question.promptTrace.input}`}
              </pre>
              <span className="mini">{question.promptTrace.provider} · {question.promptTrace.model}</span>
            </details>
          ) : null}
          <div className="step-pager">
            <span className="mini">✦ adaptive interview · {pack?.name}</span>
            <button className="btn ghost" disabled={busy || thinking} onClick={() => { setQuestion(null); void loadNext(true); }}>
              {t("session.next")}
            </button>
          </div>
        </>
      ) : (
        <div className="empty-note" style={{ borderStyle: "solid", borderColor: "var(--phosphor-deep)" }}>
          ✓ {question?.rationale || t("session.done.title")}
        </div>
      )}

      <div className="specimen" style={{ marginTop: 30 }}>
        <div className="spec-tab">
          <span className="spec-id"><b>interpretation.propose</b> · synthesize</span>
          <span className="spec-fam">stage 02</span>
        </div>
        <div className="spec-body">
          <p className="page-desc" style={{ marginBottom: 14 }}>{t("session.done.desc")}</p>
          <div className="row">
            <button
              className="btn primary"
              disabled={asked === 0 || busy}
              onClick={async () => {
                const ack = await run("interpretation.propose", { sessionId, locale });
                if (ack?.goto) navigate(ack.goto.slice(1));
              }}
            >
              {busy ? t("session.proposing") : t("session.propose")}
            </button>
            <button className="btn ghost" disabled={busy} onClick={() => void run("session.close", { sessionId })}>
              {t("session.close")}
            </button>
          </div>
          {status ? <p className="mini" style={{ marginTop: 12 }}>{status}</p> : null}
        </div>
      </div>
    </div>
  );
}
